/**
 * Phase 7.3 — Inventory PDF download helpers.
 *
 * Each function builds the query string from the current filters and
 * triggers a blob download. Returns the resolved axios response so the
 * caller can flip a loading state and toast on success/failure.
 *
 * The 413 case (server cap exceeded) bubbles up via the rejected
 * promise; callers detect it via `err?.response?.status === 413` and
 * show a "refine filters" toast.
 */
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function pickToken() {
  return localStorage.getItem("auth_token");
}

function inferFilename(headers, fallback) {
  const cd = headers?.["content-disposition"] || "";
  const m = cd.match(/filename="([^"]+)"/);
  return m ? m[1] : fallback;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function csvOrNull(arr) {
  if (!arr || arr.length === 0) return null;
  return arr.join(",");
}

/**
 * Build query params shared across all 5 endpoints.
 */
function commonParams(filters, opts = {}) {
  const p = {};
  if (filters?.preset) p.preset = filters.preset;
  if (filters?.date_from) p.date_from = filters.date_from;
  if (filters?.date_to) p.date_to = filters.date_to;
  const sources = csvOrNull(filters?.source_reports);
  if (sources) p.source_reports = sources;
  const ctypes = csvOrNull(filters?.canonical_types);
  if (ctypes) p.canonical_types = ctypes;
  if (opts.sortBy) p.sort_by = opts.sortBy;
  if (opts.sortDir) p.sort_dir = opts.sortDir;
  if (opts.search && opts.search.trim()) p.search = opts.search.trim();
  return p;
}

async function downloadPdf(path, params) {
  const url = `${API_BASE}/api/inventory/${path}/export-pdf`;
  const res = await axios.get(url, {
    params,
    headers: { Authorization: `Bearer ${pickToken()}` },
    responseType: "blob",
    timeout: 120000,
  });
  const fname = inferFilename(res.headers, `inventory-${path}.pdf`);
  triggerDownload(res.data, fname);
  return res;
}

const inventoryPdf = {
  summaryTable: (filters, opts) =>
    downloadPdf("summary-table", commonParams(filters, opts)),

  monthlyPivot: (filters, opts) =>
    downloadPdf("monthly-pivot", commonParams(filters, opts)),

  deadStock: (filters, opts) => {
    const params = commonParams(filters, opts);
    if (opts?.threshold_days != null) params.threshold_days = opts.threshold_days;
    return downloadPdf("dead-stock", params);
  },

  userMovements: (filters, opts) => {
    const params = commonParams(filters, opts);
    if (opts?.include_non_warehouse != null)
      params.include_non_warehouse = opts.include_non_warehouse;
    return downloadPdf("user-movements", params);
  },

  topProducts: (filters, opts) => {
    const params = commonParams(filters, opts);
    if (opts?.metric) params.metric = opts.metric;
    if (opts?.top_n != null) params.top_n = opts.top_n;
    return downloadPdf("top-products", params);
  },
};

/** Helper for callers: returns user-facing message based on error. */
export function pdfErrorMessage(err, t) {
  const status = err?.response?.status;
  if (status === 413) {
    return t("inventory.exportPdf.tooManyRows", {
      defaultValue:
        "Demasiados resultados. Refina los filtros y vuelve a intentar.",
    });
  }
  return t("inventory.exportPdf.error", {
    defaultValue: "Fallo al exportar PDF",
  });
}

export default inventoryPdf;
